
import React, { useState } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Shield, ShieldCheck, ChevronRight, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Admin } from "@/types/admin.types";

interface AdminsOverviewCardProps {
  admins: Partial<Admin>[];
  totalAdmins: number;
  loading: boolean;
}

const AdminsOverviewCard = ({ admins, totalAdmins, loading }: AdminsOverviewCardProps) => {
  const navigate = useNavigate();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  
  const handleViewAllAdmins = () => {
    navigate("/admins");
  };

  // Animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.4, ease: "easeOut" }
    }
  };
  
  // Enhanced 3D effect with mouse tracking
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isHovering) return;
    
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    
    // Calculate mouse position relative to card center
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    
    // Normalize values (-1 to 1) and limit rotation
    const normalizedX = (x / (rect.width / 2)) * 0.5; // Reduce rotation intensity
    const normalizedY = (y / (rect.height / 2)) * 0.5;
    
    setMousePosition({ x: normalizedX, y: normalizedY });
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={cardVariants}
    >
      <Card 
        className="h-full card-3d-container"
        style={{
          transform: isHovering ? 
            `perspective(1000px) rotateY(${mousePosition.x * 5}deg) rotateX(${-mousePosition.y * 5}deg) translateZ(10px)` : 
            'perspective(1000px) rotateY(0) rotateX(0) translateZ(0)',
          boxShadow: isHovering ? 
            '0 15px 30px rgba(0, 0, 0, 0.15), 0 0 20px rgba(0, 119, 119, 0.1)' : 
            '0 5px 15px rgba(0, 0, 0, 0.1)',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease'
        }}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => {
          setIsHovering(false);
          setMousePosition({ x: 0, y: 0 });
        }}
      >
        <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
          <CardTitle className="text-xl font-bold">Administrators</CardTitle>
          <Shield className="h-5 w-5 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mr-4">
                <ShieldCheck className={`h-6 w-6 text-primary ${isHovering ? "float-icon" : ""}`} />
              </div>
              <div>
                <p className="text-2xl font-bold">{loading ? "-" : totalAdmins}</p>
                <p className="text-sm text-muted-foreground">Total Administrators</p>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="text-sm font-medium">Recent Administrators</h4>
              
              {loading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-5 w-5 text-muted-foreground animate-spin" />
                </div>
              ) : admins.length === 0 ? (
                <p className="text-sm text-muted-foreground py-3 text-center">No administrators found</p>
              ) : (
                <div className="space-y-2">
                  {admins.slice(0, 3).map((admin) => (
                    <div key={admin.id} className="flex items-center p-2 rounded-md hover:bg-muted/50 transition-colors">
                      <Avatar className="h-8 w-8 mr-3">
                        <AvatarImage src={admin.profileImage || ''} alt={`${admin.firstName || ''} ${admin.lastName || ''}`} />
                        <AvatarFallback>{admin.firstName?.charAt(0) || ''}{admin.lastName?.charAt(0) || ''}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{admin.firstName || ''} {admin.lastName || ''}</p>
                        <p className="text-xs text-muted-foreground truncate">{admin.role || 'Admin'}</p>
                      </div>
                      <div className="flex-shrink-0">
                        <span className={`inline-block w-2 h-2 rounded-full ${admin.status === 'Active' ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Button 
              variant="outline" 
              size="sm" 
              className="w-full gap-1" 
              onClick={handleViewAllAdmins}
            >
              View All
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default AdminsOverviewCard;
